import React, { useState } from 'react';
import { staffTrackLabel, staffVisibleFiles } from '../../services/studentTracksModel';
import { removeTrackFile, restoreTrackFile, setTrackFileLabel, setTrackSongName } from '../../services/StudentTracksService';
import './TrackEditor.css';

/**
 * Edit tracks (2026-09-29, admin / owner only). Anthony: "I need a way to
 * remove tracks from a student's profile / lesson dashboard. Also a way to
 * edit titles." Decided the same evening: removal HIDES, restorable (the
 * repertoire-removal pattern of 09-27); a rename is the one title on every
 * surface, the family Songs page included; both the song's name and each
 * file's label are editable.
 *
 * Renders IN FLOW under the strip it edits, never as a popover: a positioned
 * panel inside a list row paints under later rows (CLAUDE.md). Every control
 * is always rendered, with no hover state carrying information.
 *
 * `docs` are pointer docs with applyTrackEdits already run (the service does
 * it on every fetch), so each file carries `editKey` and `label`, and
 * `removedFiles` holds what an admin hid. `onChanged` re-reads them.
 */
const standardLabel = (f) => staffTrackLabel({ ...f, label: null });

const TrackEditor = ({ docs = [], uid = null, onChanged = null, onClose = null, showSongName = true }) => {
	const [drafts, setDrafts] = useState({}); // key -> text being typed
	const [busy, setBusy] = useState(null); // key of the write in flight
	const [confirming, setConfirming] = useState(null); // editKey awaiting "Remove"
	const [error, setError] = useState('');
	const list = (Array.isArray(docs) ? docs : []).filter(Boolean);
	if (list.length === 0) return null;

	const run = async (key, write) => {
		// Guard the handler, not only the button (CLAUDE.md: a disabled state is
		// never the only guard on an action).
		if (busy) return;
		setBusy(key);
		setError('');
		try {
			await write();
			setDrafts((d) => {
				const next = { ...d };
				delete next[key];
				return next;
			});
			setConfirming(null);
			if (onChanged) onChanged();
		} catch (err) {
			console.error('Track edit failed:', err);
			setError(err && err.message ? err.message : String(err));
		} finally {
			setBusy(null);
		}
	};

	const draftOf = (key, fallback) => (Object.prototype.hasOwnProperty.call(drafts, key) ? drafts[key] : fallback);
	const setDraft = (key, value) => setDrafts((d) => ({ ...d, [key]: value }));

	return (
		<div className="trk-edit" data-testid="track-editor">
			<div className="trk-edit__top">
				<span className="trk-edit__title">Edit tracks</span>
				{onClose && (
					<button type="button" className="trk-edit__btn" onClick={onClose}>
						Done
					</button>
				)}
			</div>

			{list.map((doc) => {
				const nameKey = `name:${doc.id}`;
				const nameValue = draftOf(nameKey, doc.songName || '');
				const nameChanged = nameValue.trim() !== String(doc.songName || '').trim();
				const files = staffVisibleFiles(doc.files);
				const removed = Array.isArray(doc.removedFiles) ? doc.removedFiles : [];
				return (
					<div className="trk-edit__doc" key={doc.id}>
						{showSongName && (
							<form
								className="trk-edit__row"
								onSubmit={(e) => {
									e.preventDefault();
									if (!nameChanged) return;
									run(nameKey, () => setTrackSongName(doc.id, nameValue));
								}}>
								<label className="trk-edit__field">
									<span className="trk-edit__label">Song name</span>
									<input
										className="trk-edit__input"
										type="text"
										value={nameValue}
										maxLength={120}
										placeholder={doc.originalSongName || 'Song name'}
										onChange={(e) => setDraft(nameKey, e.target.value)}
									/>
								</label>
								<button type="submit" className="trk-edit__btn trk-edit__btn--primary" disabled={!nameChanged || Boolean(busy)}>
									{busy === nameKey ? 'Saving…' : 'Save'}
								</button>
								{doc.originalSongName && doc.songName !== doc.originalSongName && (
									<p className="trk-edit__hint">
										Filed as {doc.originalSongName}. Clear the name and save to go back to it.
									</p>
								)}
							</form>
						)}

						{files.length === 0 && <p className="trk-edit__hint">No tracks showing for this song.</p>}
						<ul className="trk-edit__list">
							{files.map((f) => {
								const key = f.editKey;
								const shown = f.label || standardLabel(f);
								// Pre-filled with the title in use, so a rename and the
								// standard name never look alike; saving the standard name
								// (or nothing) clears the rename.
								const value = draftOf(key, shown);
								const changed = value.trim() !== shown;
								const toStore = value.trim() === standardLabel(f) ? '' : value;
								return (
									<li className="trk-edit__file" key={key}>
										<form
											className="trk-edit__row"
											onSubmit={(e) => {
												e.preventDefault();
												if (!changed) return;
												run(key, () => setTrackFileLabel(doc.id, key, toStore));
											}}>
											<label className="trk-edit__field">
												<span className="trk-edit__label">
													{f.length ? `${f.length} · ` : ''}
													{standardLabel(f)}
												</span>
												<input
													className="trk-edit__input"
													type="text"
													value={value}
													maxLength={120}
													placeholder={standardLabel(f)}
													aria-label={`Title for ${shown}`}
													onChange={(e) => setDraft(key, e.target.value)}
												/>
											</label>
											<button type="submit" className="trk-edit__btn trk-edit__btn--primary" disabled={!changed || Boolean(busy)}>
												{busy === key ? 'Saving…' : 'Save'}
											</button>
											{confirming === key ? null : (
												<button
													type="button"
													className="trk-edit__btn trk-edit__btn--danger"
													onClick={() => {
														if (busy) return;
														setConfirming(key);
													}}>
													<i className="fa-sharp fa-solid fa-trash" aria-hidden="true" />
													Remove
												</button>
											)}
										</form>
										{confirming === key && (
											<div className="trk-edit__confirm" role="group" aria-label={`Remove ${shown}`}>
												<span>
													Remove {shown}? It disappears from lessons, the profile and the family's Songs page. You can restore
													it under Removed tracks.
												</span>
												<button
													type="button"
													className="trk-edit__btn trk-edit__btn--danger"
													disabled={Boolean(busy)}
													onClick={() => run(key, () => removeTrackFile(doc.id, key, uid))}>
													{busy === key ? 'Removing…' : 'Remove'}
												</button>
												<button type="button" className="trk-edit__btn" onClick={() => setConfirming(null)}>
													Cancel
												</button>
											</div>
										)}
									</li>
								);
							})}
						</ul>

						{removed.length > 0 && (
							<div className="trk-edit__removed">
								<span className="trk-edit__label">Removed</span>
								<ul className="trk-edit__list">
									{removed.map((f) => (
										<li className="trk-edit__file trk-edit__file--removed" key={f.editKey}>
											<span className="trk-edit__name">
												{f.length ? `${f.length} · ` : ''}
												{f.label || standardLabel(f)}
											</span>
											<button
												type="button"
												className="trk-edit__btn"
												disabled={Boolean(busy)}
												onClick={() => run(`restore:${f.editKey}`, () => restoreTrackFile(doc.id, f.editKey))}>
												{busy === `restore:${f.editKey}` ? 'Restoring…' : 'Restore'}
											</button>
										</li>
									))}
								</ul>
							</div>
						)}
					</div>
				);
			})}

			{error && (
				<p className="trk-edit__error" role="alert">
					That change could not be saved. Please tell Anthony right away.
					<span className="trk-edit__detail">{error}</span>
				</p>
			)}
		</div>
	);
};

/** The always-visible control that opens the editor (admins only; callers gate it). */
export const EditTracksButton = ({ open, onClick, label = 'Edit tracks' }) => (
	<button type="button" className={`trk-edit__open${open ? ' is-on' : ''}`} aria-expanded={open} onClick={onClick}>
		<i className="fa-sharp fa-solid fa-pen" aria-hidden="true" />
		{label}
	</button>
);

export default TrackEditor;
